import React, { useState, FormEvent } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "info@codeflair.com",
      description: "Send us an email anytime",
    },
    {
      icon: Phone,
      title: "Phone",
      value: "+91-9636333160",
      description: "Mon-Fri from 8am to 6pm",
    },
    {
      icon: MapPin,
      title: "Office",
      value: "A-17, Vivek Vihar, Gandhipath(west), Jaipur, India - 302021",
      description: "Visit us anytime",
    },
    {
      icon: Clock,
      title: "Working Hours",
      value: "Mon - Fri: 9AM - 6PM",
      description: "Weekend by appointment",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white section">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="mb-6 text-5xl font-bold lg:text-6xl text-dark-gray">Get In Touch</h1>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-medium-gray">
              Ready to start your project? Let's discuss how we can help bring your vision to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="bg-white section">
        <div className="container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Form */}
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h2 className="mb-6 text-3xl font-bold text-dark-gray">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block mb-2 text-sm font-medium text-dark-gray">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-dark-gray">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className="block mb-2 text-sm font-medium text-dark-gray">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      placeholder="+91-9414051941"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block mb-2 text-sm font-medium text-dark-gray">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block mb-2 text-sm font-medium text-dark-gray">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors resize-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button type="submit" disabled={isSubmitting} className="flex justify-center items-center w-full btn btn-primary">
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send size={20} className="ml-2" />
                    </>
                  )}
                </button>

                {submitStatus === "success" && (
                  <div className="p-4 text-green-800 bg-green-50 rounded-lg border border-green-200">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="p-4 text-red-800 bg-red-50 rounded-lg border border-red-200">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <h2 className="mb-6 text-3xl font-bold text-dark-gray">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="p-3 bg-opacity-10 rounded-lg bg-light-gray">
                      <info.icon size={24} className="text-primary-blue" />
                    </div>
                    <div>
                      <h3 className="mb-1 text-lg font-semibold text-dark-gray">{info.title}</h3>
                      <p className="mb-1 font-medium text-primary-blue">{info.value}</p>
                      <p className="text-sm text-medium-gray">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 mt-8 rounded-xl bg-light-gray">
                <h3 className="mb-4 text-xl font-semibold text-dark-gray">Why Choose Codeflair?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center text-medium-gray">
                    <div className="mr-3 w-2 h-2 bg-gray-400 rounded-full"></div>
                    Free consultation and project planning
                  </li>
                  <li className="flex items-center text-medium-gray">
                    <div className="mr-3 w-2 h-2 bg-gray-400 rounded-full"></div>
                    Transparent pricing and timelines
                  </li>
                  <li className="flex items-center text-medium-gray">
                    <div className="mr-3 w-2 h-2 bg-gray-400 rounded-full"></div>
                    Ongoing support and maintenance
                  </li>
                  <li className="flex items-center text-medium-gray">
                    <div className="mr-3 w-2 h-2 bg-gray-400 rounded-full"></div>
                    Modern technologies and best practices
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
